import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import styles from "./StageCascade.module.css";

// A staged drop-off cascade: a linear spine of gates where flow advances, stays
// open, or exits at each gate. The surviving spine runs flat along the top and
// ends in `completed`; at each gate the cohort sheds whatever stays open (the
// bar's unflowed middle, shown as a faint count, no ribbon) and whatever exits
// (terminal leaves peeling off below). The component carries no domain
// knowledge. Callers describe the flow with the props below, and cumulative
// counts, layout, and conservation are all derived here.

export type CascadeStage = { id: string; label: string; color: string };

export type CascadeLeaf = {
  stageId: string; // the gate this peels off at
  kind: "stay" | "exit"; // open-at-this-gate vs. terminal exit
  label: string;
  color: string;
  value: number;
};

// The flow that made it through the final gate (the spine's terminal "win").
export type CascadeCompleted = { label: string; color: string; value: number };

export type StageCascadeProps = {
  stages: CascadeStage[]; // ordered spine, entry gate first
  leaves: CascadeLeaf[]; // everything that peels off (zero-value dropped)
  completed: CascadeCompleted;
  formatValue?: (n: number) => string;
  formatPct?: (fraction: number) => string;
  emptyMessage?: string;
};

type NodeKind = "gate" | "good" | "stay" | "exit";

type LayoutNode = {
  id: string;
  label: string;
  color: string;
  value: number;
  depth: number;
  x: number;
  yTop: number;
  height: number;
  kind: NodeKind;
};

type LayoutLink = {
  id: string;
  color: string;
  sx: number;
  sTop: number;
  sBot: number;
  tx: number;
  tTop: number;
  tBot: number;
};

type StubAnnotation = { x: number; y: number; value: number };

type Layout = {
  nodes: LayoutNode[];
  links: LayoutLink[];
  stubs: StubAnnotation[];
  height: number;
  total: number;
  byId: Record<string, LayoutNode>;
  entryId: string;
};

const COMPLETED_ID = "__completed__";

const BAR_PX = 300;
const GAP = 16;
const TOP_PAD = 26;
const BOT_PAD = 16;
const NODE_W = 12;
const LEFT = 110;
const RIGHT_LABEL = 190;

const defaultPct = (f: number): string => `${Math.round(f * 100)}%`;

const computeLayout = (
  stages: CascadeStage[],
  leaves: CascadeLeaf[],
  completed: CascadeCompleted,
  width: number
): Layout | null => {
  const n = stages.length;
  const entry = stages[0];
  if (n === 0 || !entry) return null;

  const stageIndex: Record<string, number> = {};
  stages.forEach((s, i) => {
    stageIndex[s.id] = i;
  });

  const live = leaves.filter(
    (l) => l.value > 0 && stageIndex[l.stageId] !== undefined
  );

  // cumulative[i] = count reaching gate i = completed + everything shed at i..n-1.
  // Deriving it here (rather than taking it from the caller) makes the spine
  // reconcile with the leaves by construction.
  const shed = new Array(n).fill(0) as number[];
  for (const l of live) {
    const si = stageIndex[l.stageId] ?? 0;
    shed[si] = (shed[si] ?? 0) + l.value;
  }
  const cum = new Array(n + 1).fill(0) as number[];
  cum[n] = completed.value;
  for (let i = n - 1; i >= 0; i--) cum[i] = (cum[i + 1] ?? 0) + (shed[i] ?? 0);
  const total = cum[0] ?? 0;
  if (total <= 0) return null;

  const innerW = Math.max(width - LEFT - RIGHT_LABEL - NODE_W, 320);
  const colX = (depth: number): number => LEFT + (innerW / n) * depth;
  const scale = BAR_PX / total;

  // Leaf descriptors: stays/exits at depth (stage+1); completed at depth n.
  const leafDescs: Record<
    string,
    {
      label: string;
      color: string;
      value: number;
      kind: NodeKind;
      depth: number;
    }
  > = {};
  const byStage: Record<string, { stay: string[]; exit: string[] }> = {};
  live.forEach((l, i) => {
    const id = `leaf-${i}`;
    leafDescs[id] = {
      label: l.label,
      color: l.color,
      value: l.value,
      kind: l.kind,
      depth: (stageIndex[l.stageId] ?? 0) + 1,
    };
    const g = (byStage[l.stageId] ??= { stay: [], exit: [] });
    (l.kind === "stay" ? g.stay : g.exit).push(id);
  });
  if (completed.value > 0) {
    leafDescs[COMPLETED_ID] = {
      label: completed.label,
      color: completed.color,
      value: completed.value,
      kind: "good",
      depth: n,
    };
  }

  // Each gate's children top-to-bottom: advance (good) → stays → exits.
  const childrenOf: Record<string, string[]> = {};
  for (let i = 0; i < n; i++) {
    if ((cum[i] ?? 0) <= 0) continue;
    const stage = stages[i];
    if (!stage) continue;
    const kids: string[] = [];
    if ((cum[i + 1] ?? 0) > 0) {
      const next = stages[i + 1];
      kids.push(i + 1 < n && next ? next.id : COMPLETED_ID);
    }
    const g = byStage[stage.id];
    if (g) {
      for (const id of g.stay) kids.push(id);
      for (const id of g.exit) kids.push(id);
    }
    childrenOf[stage.id] = kids;
  }

  // DFS (spine-first) → planar top-to-bottom leaf order.
  const leafOrder: string[] = [];
  const walk = (gateId: string): void => {
    for (const cId of childrenOf[gateId] ?? []) {
      if (childrenOf[cId]) walk(cId);
      else leafOrder.push(cId);
    }
  };
  walk(entry.id);

  const byId: Record<string, LayoutNode> = {};
  let y = TOP_PAD;
  for (const id of leafOrder) {
    const ld = leafDescs[id];
    if (!ld) continue;
    const h = Math.max(ld.value * scale, 2);
    byId[id] = {
      id,
      label: ld.label,
      color: ld.color,
      value: ld.value,
      depth: ld.depth,
      x: colX(ld.depth),
      yTop: y,
      height: h,
      kind: ld.kind,
    };
    y += h + GAP;
  }
  const height = y - GAP + BOT_PAD;

  // Gate bars top-aligned; height = sum of children so a gate shrinks by exactly
  // what peeled off before it. Inner-to-outer so each gate sees its children.
  for (let i = n - 1; i >= 0; i--) {
    const stage = stages[i];
    if (!stage) continue;
    const kids = childrenOf[stage.id];
    if (!kids) continue;
    const childNodes = kids
      .map((c) => byId[c])
      .filter((k): k is LayoutNode => !!k);
    if (childNodes.length === 0) continue;
    byId[stage.id] = {
      id: stage.id,
      label: stage.label,
      color: stage.color,
      value: cum[i] ?? 0,
      depth: i,
      x: colX(i),
      yTop: TOP_PAD,
      height: childNodes.reduce((s, k) => s + k.height, 0),
      kind: "gate",
    };
  }

  const links: LayoutLink[] = [];
  const stubs: StubAnnotation[] = [];
  for (let i = 0; i < n; i++) {
    const stage = stages[i];
    if (!stage) continue;
    const parent = byId[stage.id];
    if (!parent) continue;
    let by = parent.yTop;
    for (const cId of childrenOf[stage.id] ?? []) {
      const child = byId[cId];
      if (!child) continue;
      if (child.kind === "stay") {
        // No ribbon: the stay is the bar's unflowed middle, marked by a count.
        stubs.push({
          x: parent.x + NODE_W + 6,
          y: by + child.height / 2 + 3,
          value: child.value,
        });
        by += child.height;
        continue;
      }
      links.push({
        id: `${stage.id}-${cId}`,
        color: child.color,
        sx: parent.x + NODE_W,
        sTop: by,
        sBot: by + child.height,
        tx: child.x,
        tTop: child.yTop,
        tBot: child.yTop + child.height,
      });
      by += child.height;
    }
  }

  return {
    nodes: Object.values(byId),
    links,
    stubs,
    height,
    total,
    byId,
    entryId: entry.id,
  };
};

const linkPath = (l: LayoutLink): string => {
  const cx = (l.sx + l.tx) / 2;
  return [
    `M ${l.sx} ${l.sTop}`,
    `C ${cx} ${l.sTop} ${cx} ${l.tTop} ${l.tx} ${l.tTop}`,
    `L ${l.tx} ${l.tBot}`,
    `C ${cx} ${l.tBot} ${cx} ${l.sBot} ${l.sx} ${l.sBot}`,
    "Z",
  ].join(" ");
};

const Diagram = ({
  stages,
  leaves,
  completed,
  width,
  formatValue,
  formatPct,
  emptyMessage,
}: Required<Omit<StageCascadeProps, "formatValue" | "formatPct">> & {
  width: number;
  formatValue: (n: number) => string;
  formatPct: (fraction: number) => string;
}) => {
  const layout = useMemo(
    () => computeLayout(stages, leaves, completed, width),
    [stages, leaves, completed, width]
  );
  if (!layout) {
    return emptyMessage ? (
      <div className={styles.empty}>{emptyMessage}</div>
    ) : null;
  }
  const { nodes, links, stubs, height, total, byId, entryId } = layout;
  const pct = (v: number): string => formatPct(v / total);
  const entry = byId[entryId];

  return (
    <svg width={width} height={height} className={styles.svg}>
      {links.map((l) => (
        <path key={l.id} d={linkPath(l)} fill={l.color} fillOpacity={0.4} />
      ))}
      {nodes
        .filter((n) => n.kind !== "stay")
        .map((n) => (
          <rect
            key={n.id}
            x={n.x}
            y={n.yTop}
            width={NODE_W}
            height={n.height}
            rx={2}
            fill={n.color}
          />
        ))}

      {/* The entry gate has no upstream bar to align to, so it labels to its
          left; every later gate labels above its bar. */}
      {entry && (
        <text
          x={entry.x - 10}
          y={entry.yTop + entry.height / 2}
          textAnchor="end"
        >
          <tspan x={entry.x - 10} dy={-2} className={styles.rootName}>
            {entry.label}
          </tspan>
          <tspan x={entry.x - 10} dy={16} className={styles.rootCount}>
            {formatValue(entry.value)}
          </tspan>
        </text>
      )}

      {stages.slice(1).map((s) => {
        const node = byId[s.id];
        if (!node) return null;
        return (
          <text
            key={s.id}
            className={styles.gateLabel}
            x={node.x + NODE_W / 2}
            y={node.yTop - 7}
            textAnchor="middle"
          >
            {node.label} · {pct(node.value)}
          </text>
        );
      })}

      {/* Terminal and win leaves label to their right; the cascade ordering
          keeps the space there clear at each leaf's band. */}
      {nodes
        .filter((n) => n.kind === "good" || n.kind === "exit")
        .map((n) => (
          <text
            key={`lbl-${n.id}`}
            x={n.x + NODE_W + 10}
            y={n.yTop + n.height / 2 + 4}
          >
            <tspan className={styles.leafName}>{n.label}</tspan>
            <tspan dx={6} className={styles.leafCount}>
              {formatValue(n.value)} · {pct(n.value)}
            </tspan>
          </text>
        ))}

      {/* Faint count of the still-open remainder in each gate bar's middle. */}
      {stubs.map((s, i) => (
        <text key={`stub-${i}`} className={styles.stubCount} x={s.x} y={s.y}>
          {formatValue(s.value)}
        </text>
      ))}
    </svg>
  );
};

export const StageCascade = ({
  stages,
  leaves,
  completed,
  formatValue = String,
  formatPct = defaultPct,
  emptyMessage = "",
}: StageCascadeProps) => (
  <div className={styles.wrap}>
    <ParentSize>
      {({ width }) =>
        width > 0 ? (
          <Diagram
            stages={stages}
            leaves={leaves}
            completed={completed}
            width={width}
            formatValue={formatValue}
            formatPct={formatPct}
            emptyMessage={emptyMessage}
          />
        ) : null
      }
    </ParentSize>
  </div>
);
